import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../api/apiClient';
import '../styles/PDFDownloadButton.css';
import '../styles/ReportPreview.css';
import { saveBlobFile } from '../utils/saveFile';

/**
 * One "Report" button. Clicking it opens a preview of the report; the preview
 * has the Download PDF action. Both come from the backend
 * (/api/monitoring/report), so the web and React Native apps get the EXACT
 * same report:
 *   preview  -> ?format=html  (the HTML the PDF is rendered from; works in
 *                              every browser and in the iOS/Android WebView)
 *   download -> ?format=pdf   (default)
 *
 * Props are unchanged, so DesktopLayout / MobileLayout need no edits:
 *   deviceId, period ('daily'|'weekly'|'monthly'), date (YYYY-MM-DD),
 *   deviceInfo { serialNumber }, isReportReady, onGenerate(success)
 */
const PERIOD_LABEL = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };

async function readError(response) {
  let msg = `Server returned ${response.status}`;
  try {
    const data = await response.json();
    if (data && data.error) msg = data.error;
  } catch (_) {
    /* not JSON */
  }
  return msg;
}

const PDFDownloadButton = ({
  deviceId,
  period = 'daily',
  date,
  deviceInfo = {},
  isReportReady = true,
  onGenerate,
}) => {
  const [open, setOpen] = useState(false);
  const [html, setHtml] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const abortRef = useRef(null);

  const periodLabel = PERIOD_LABEL[period] || 'Daily';
  const isDisabled = !deviceId || !isReportReady;

  const reportUrl = (format) =>
    `/api/monitoring/report` +
    `?device=${encodeURIComponent(deviceId)}` +
    `&date=${encodeURIComponent(date)}` +
    `&period=${encodeURIComponent(period)}` +
    `&format=${format}`;

  // ── Preview ────────────────────────────────────────────────────────────
  const loadPreview = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setIsLoadingPreview(true);
    setPreviewError(null);
    setHtml('');
    try {
      const response = await apiFetch(reportUrl('html'), { signal: ctrl.signal });
      if (!response.ok) throw new Error(await readError(response));
      const text = await response.text();
      if (!ctrl.signal.aborted) setHtml(text);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Report preview error:', err);
        setPreviewError(err.message || 'Could not load the preview');
      }
    } finally {
      if (!ctrl.signal.aborted) setIsLoadingPreview(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, date, period]);

  const openPreview = () => {
    if (isDisabled) return;
    setOpen(true);
    setDownloadError(null);
    loadPreview();
  };

  const closePreview = useCallback(() => {
    abortRef.current?.abort();
    setOpen(false);
    setHtml('');
    setIsLoadingPreview(false);
  }, []);

  // Esc to close + lock page scroll while the preview is open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && closePreview();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, closePreview]);

  useEffect(() => () => abortRef.current?.abort(), []);

  // ── Download ───────────────────────────────────────────────────────────
  const handleDownload = async () => {
    if (isDownloading) return;
    try {
      setIsDownloading(true);
      setDownloadError(null);
      const response = await apiFetch(reportUrl('pdf'));
      if (!response.ok) throw new Error(await readError(response));
      const blob = await response.blob();
      const filename = `router-report-${period}-${deviceInfo.serialNumber || 'device'}-${date}.pdf`;
      // Browser: normal download. iOS / Android app: writes to cache and
      // opens the share sheet (which also lets the user view the PDF).
      await saveBlobFile(blob, filename, { dialogTitle: 'Save PDF report' });
      if (onGenerate) onGenerate(true);
    } catch (err) {
      console.error('PDF download error:', err);
      setDownloadError(err.message || 'Failed to generate PDF');
      if (onGenerate) onGenerate(false);
    } finally {
      setIsDownloading(false);
    }
  };

  const title = !deviceId
    ? 'No device selected'
    : !isReportReady
      ? 'Preparing report data, please wait…'
      : `Preview & download the ${periodLabel.toLowerCase()} report`;

  return (
    <div className="pdf-download-container">
      <button className="pdf-download-btn" onClick={openPreview} disabled={isDisabled} title={title}>
        {!isReportReady && deviceId ? (
          <>
            <span className="pdf-spinner"></span>
            Preparing...
          </>
        ) : (
          <>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#112023" strokeWidth="1.4">
              <path d="M3 1.5h6.5L13 5v9.5H3z"></path>
              <path d="M9.5 1.5V5H13M5.5 8.5h5M5.5 11h3.5"></path>
            </svg>
            {periodLabel} report
          </>
        )}
      </button>

      {open && (
        <div className="rp-overlay" role="dialog" aria-modal="true" aria-label={`${periodLabel} report preview`}
             onMouseDown={(e) => e.target === e.currentTarget && closePreview()}>
          <div className="rp-dialog">
            <div className="rp-bar">
              <div className="rp-title">
                <span className="rp-eyebrow">PREVIEW</span>
                <span>{periodLabel} report · {date}</span>
              </div>
              <div className="rp-actions">
                <button type="button" className="rp-btn rp-btn-primary" onClick={handleDownload}
                        disabled={isDownloading}>
                  {isDownloading ? (<><span className="pdf-spinner"></span>Generating…</>) : 'Download PDF'}
                </button>
                <button type="button" className="rp-btn" onClick={closePreview} aria-label="Close preview">
                  ✕
                </button>
              </div>
            </div>

            {downloadError && <div className="rp-error">{downloadError}</div>}

            <div className="rp-body">
              {isLoadingPreview && (
                <div className="rp-state"><span className="pdf-spinner"></span>Building report…</div>
              )}
              {previewError && !isLoadingPreview && (
                <div className="rp-state">
                  <p>{previewError}</p>
                  <button type="button" className="rp-btn" onClick={loadPreview}>Try again</button>
                </div>
              )}
              {html && !isLoadingPreview && (
                // sandbox="" = no scripts, no navigation; the report is static HTML.
                <iframe className="rp-frame" title="Report preview" sandbox="" srcDoc={html} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDFDownloadButton;
